import type { HeatmapEventVisibility } from '@repo/store/maps/slices/mapData';
import type * as MapLibre from 'maplibre-gl';
import type { ExpressionSpecification, FilterSpecification, Map as MapLibreMap } from 'maplibre-gl';
import { FOG_LAYER_ID } from '../../../fog-of-war/fog-of-war-layer';
import type { HeatmapTitleConfig } from '../../data/heatmap-config';
import { getHeatmapEventOption } from '../../data/heatmap-events';
import type { HeatmapFeatureCollection } from '../../data/heatmap-types';
import { applyHeatmapWeights } from '../../data/heatmap-weight';
import { getEventIconSvg } from '../fontUtils';
import { LAYER_CONFIG } from '../object3d-constants';
import {
    areaLayerId,
    CLUSTER_FILL_OPACITY,
    CLUSTER_OUTLINE_OPACITY,
    CLUSTER_OUTLINE_WIDTH,
    DEFAULT_CLUSTER_COLOR,
    DEFAULT_MARKER_SORT_KEY,
    HOVER_FILTER_NONE,
    HOVERED_MARKER_SORT_KEY,
    heatLayerId,
    ICON_SCALE,
    ICON_SCALE_HOVERED,
    LAYER_ID_PREFIX,
    MARKER_HOVER_LAYER_ID,
    MARKER_ICON_INSET_PX,
    MARKER_ICON_SIZE_PX,
    MARKER_IMAGE_PIXEL_RATIO,
    MARKER_IMAGE_PREFIX,
    MARKER_IMAGE_SIZE_PX,
    markerLayerId,
    outlineLayerId,
    SOURCE_ID,
} from './constants';
import { heatLayerPaint } from './paint';

function toHeatmapGeoJson(collection: HeatmapFeatureCollection): GeoJSON.FeatureCollection {
    const geojson = buildHeatmapGeoJson(collection);
    applyHeatmapWeights({ geojson });
    return geojson;
}

function buildHeatmapGeoJson(collection: HeatmapFeatureCollection): GeoJSON.FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: collection.features.map(feature => {
            const properties = {
                kind: feature.properties.kind,
                event_type: feature.properties.event_type,
                rank: feature.properties.rank,
                events: feature.properties.events,
                share: feature.properties.share,
                /** Normalized density weight; filled in by `applyHeatmapWeights` below. */
                w: 0,
                radius_world: feature.properties.radius_world,
                feature_id: feature.id,
                // JSON string — MapLibre feature props are scalars; parse on select.
                video_ids:
                    feature.properties.video_ids && feature.properties.video_ids.length > 0
                        ? JSON.stringify(feature.properties.video_ids)
                        : undefined,
            };

            if (feature.geometry.type === 'Polygon') {
                return {
                    type: 'Feature' as const,
                    id: feature.id,
                    properties,
                    geometry: {
                        type: 'Polygon' as const,
                        coordinates: feature.geometry.coordinates.map(ring =>
                            ring.map(coord => [coord[0], coord[1]] as [number, number]),
                        ),
                    },
                };
            }

            return {
                type: 'Feature' as const,
                id: feature.id,
                properties,
                geometry: {
                    type: 'Point' as const,
                    coordinates: [feature.geometry.coordinates[0], feature.geometry.coordinates[1]],
                },
            };
        }),
    };
}

/**
 * Insert heat density above tiles/fog, below pin markers / 3D / trails.
 * Cluster coverage disks + markers are raised separately (see `raiseHeatmapClusterOverlayLayers`).
 */
function getHeatmapInsertBeforeLayerId(map: MapLibreMap): string | undefined {
    const layers = map.getStyle()?.layers;
    if (!layers) return undefined;
    for (const layer of layers) {
        if (layer.id === 'map-tiles') continue;
        if (layer.type === 'background') continue;
        if (layer.id === FOG_LAYER_ID) continue;
        if (layer.id.startsWith(LAYER_ID_PREFIX)) continue;
        if (layer.id.startsWith(LAYER_CONFIG.LAYER_ID_PREFIX)) continue;
        return layer.id;
    }
    return undefined;
}

function moveLayerToTop(map: MapLibreMap, layerId: string): void {
    if (!map.getLayer(layerId)) return;
    try {
        map.moveLayer(layerId);
    } catch {
        // Layer mid-update.
    }
}

/**
 * Keep cluster coverage disks + hotspot markers above regular map pins.
 * Order: fill → outline → markers → hover (so symbols stay on top of the rings).
 */
export function raiseHeatmapClusterOverlayLayers(map: MapLibreMap, eventTypes: readonly string[]): void {
    for (const eventType of eventTypes) {
        moveLayerToTop(map, areaLayerId(eventType));
        moveLayerToTop(map, outlineLayerId(eventType));
    }
    for (const eventType of eventTypes) {
        moveLayerToTop(map, markerLayerId(eventType));
    }
    moveLayerToTop(map, MARKER_HOVER_LAYER_ID);
}

function clusterColor(eventType: string, config: HeatmapTitleConfig): string {
    return getHeatmapEventOption(config, eventType)?.color ?? DEFAULT_CLUSTER_COLOR;
}

function markerImageId(eventType: string): string {
    return `${MARKER_IMAGE_PREFIX}${eventType}`;
}

export function categoryKeys(collection: HeatmapFeatureCollection): readonly string[] {
    return collection.meta.event_types;
}

export function allMarkerLayerIds(eventTypes: readonly string[]): string[] {
    return eventTypes.map(eventType => markerLayerId(eventType));
}

function hoverFeatureFilter(featureId: string): FilterSpecification {
    return ['==', ['get', 'feature_id'], featureId];
}

function withIconFill(iconSvg: string, iconColor: string): string {
    return iconSvg.replace(/\sfill="[^"]*"/gi, '').replace(/<path\b/gi, `<path fill="${iconColor}"`);
}

async function createMarkerImage(color: string, iconSvg: string, iconColor = '#FFFFFF'): Promise<ImageData> {
    const size = MARKER_IMAGE_SIZE_PX;
    const inset = MARKER_ICON_INSET_PX;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Could not create canvas for heatmap marker');
    }

    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#0a0a0a';
    ctx.stroke();

    const iconImg = await loadImageFromSvg(withIconFill(iconSvg, iconColor));
    ctx.drawImage(iconImg, inset, inset, MARKER_ICON_SIZE_PX, MARKER_ICON_SIZE_PX);

    return ctx.getImageData(0, 0, size, size);
}

async function loadImageFromSvg(svg: string): Promise<HTMLImageElement> {
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to decode heatmap marker SVG'));
        img.src = dataUrl;
    });
    return img;
}

async function ensureMarkerImages(
    map: MapLibreMap,
    collection: HeatmapFeatureCollection,
    config: HeatmapTitleConfig,
): Promise<void> {
    await Promise.all(
        categoryKeys(collection).map(async eventType => {
            const imageId = markerImageId(eventType);
            try {
                if (map.hasImage(imageId)) return;
            } catch {
                return;
            }

            const option = getHeatmapEventOption(config, eventType);
            if (!option) return;
            const iconSvg = await getEventIconSvg(option.icon, MARKER_ICON_SIZE_PX);
            if (!iconSvg) return;

            const imageData = await createMarkerImage(option.color, iconSvg, option.iconColor);

            try {
                if (!map.hasImage(imageId)) {
                    map.addImage(imageId, imageData, { pixelRatio: MARKER_IMAGE_PIXEL_RATIO });
                }
            } catch {
                // Style swap mid-load.
            }
        }),
    );
}

export function removeHeatmap(map: MapLibreMap): void {
    const layers = map.getStyle()?.layers;
    if (layers) {
        for (const layer of [...layers].reverse()) {
            if (!layer.id.startsWith(LAYER_ID_PREFIX)) continue;
            try {
                map.removeLayer(layer.id);
            } catch {
                // Layer mid-update.
            }
        }
    }
    if (map.getSource(SOURCE_ID)) {
        map.removeSource(SOURCE_ID);
    }
}

export function syncHeatmapVisibility(
    map: MapLibreMap,
    collection: HeatmapFeatureCollection,
    visibility: HeatmapEventVisibility,
): void {
    const eventTypes = categoryKeys(collection);
    for (const eventType of eventTypes) {
        const visible = visibility[eventType] !== false ? 'visible' : 'none';
        for (const layerId of [
            heatLayerId(eventType),
            areaLayerId(eventType),
            outlineLayerId(eventType),
            markerLayerId(eventType),
        ]) {
            if (!map.getLayer(layerId)) continue;
            try {
                map.setLayoutProperty(layerId, 'visibility', visible);
            } catch {
                // Layer mid-update.
            }
        }
    }
}

export function setHoveredMarkerOverlay(map: MapLibreMap, hoveredId: string | null): void {
    if (!map.getLayer(MARKER_HOVER_LAYER_ID)) return;
    try {
        map.setFilter(MARKER_HOVER_LAYER_ID, hoveredId ? hoverFeatureFilter(hoveredId) : HOVER_FILTER_NONE);
    } catch {
        // Layer mid-update.
    }
}

export async function addHeatmap(
    map: MapLibreMap,
    collection: HeatmapFeatureCollection,
    visibility: HeatmapEventVisibility,
    config: HeatmapTitleConfig,
    /** Return false if this attempt was superseded while awaiting images. */
    isCurrent: () => boolean,
): Promise<boolean> {
    await ensureMarkerImages(map, collection, config);

    // Marker images are async — bail before mutating so a stale collection cannot
    // overwrite the source the replacement effect just (or will) install.
    if (!isCurrent() || map._removed) return false;

    const geoJson = toHeatmapGeoJson(collection);
    const existingSource = map.getSource(SOURCE_ID) as MapLibre.GeoJSONSource | undefined;
    if (existingSource) {
        try {
            existingSource.setData(geoJson);
        } catch {
            // Source mid-update.
        }
        ensureHeatmapLayers(map, collection, visibility, config, getHeatmapInsertBeforeLayerId(map));
        syncHeatmapVisibility(map, collection, visibility);
        return true;
    }

    const beforeId = getHeatmapInsertBeforeLayerId(map);

    try {
        map.addSource(SOURCE_ID, {
            type: 'geojson',
            data: geoJson,
            promoteId: 'feature_id',
        });
    } catch {
        // Concurrent add (e.g. Strict Mode) — adopt the source that won the race.
        if (!isCurrent() || map._removed) return false;
        const racedSource = map.getSource(SOURCE_ID) as MapLibre.GeoJSONSource | undefined;
        if (!racedSource) return false;
        try {
            racedSource.setData(geoJson);
        } catch {
            // Source mid-update.
        }
        ensureHeatmapLayers(map, collection, visibility, config, beforeId);
        syncHeatmapVisibility(map, collection, visibility);
        return true;
    }

    if (!isCurrent() || map._removed) {
        // We may have just added the source for a superseded attempt — leave teardown
        // to the owning effect's cleanup / retry rather than racing remove+add here.
        return false;
    }

    ensureHeatmapLayers(map, collection, visibility, config, beforeId);
    return true;
}

function buildHoverIconImageExpression(eventTypes: readonly string[]): ExpressionSpecification | string {
    const fallbackType = eventTypes[0] ?? 'kills';
    const fallbackImage = markerImageId(fallbackType);
    if (eventTypes.length <= 1) {
        return fallbackImage;
    }

    // Build match arms as a flat list: key, value, key, value, … fallback.
    const arms: unknown[] = ['match', ['get', 'event_type']];
    for (const eventType of eventTypes) {
        arms.push(eventType, markerImageId(eventType));
    }
    arms.push(fallbackImage);
    return arms as ExpressionSpecification;
}

function ensureHeatmapLayers(
    map: MapLibreMap,
    collection: HeatmapFeatureCollection,
    visibility: HeatmapEventVisibility,
    config: HeatmapTitleConfig,
    beforeId: string | undefined,
): void {
    const eventTypes = categoryKeys(collection);

    // Soft bin density heat sits under coverage disks / markers (all zooms).
    for (const eventType of eventTypes) {
        const layerId = heatLayerId(eventType);
        const paint = heatLayerPaint(eventType);

        if (map.getLayer(layerId)) {
            try {
                for (const [key, value] of Object.entries(paint)) {
                    map.setPaintProperty(layerId, key, value);
                }
            } catch {
                // Layer mid-update.
            }
            continue;
        }

        map.addLayer(
            {
                id: layerId,
                type: 'heatmap',
                source: SOURCE_ID,
                layout: {
                    visibility: visibility[eventType] !== false ? 'visible' : 'none',
                },
                filter: [
                    'all',
                    ['==', ['get', 'kind'], 'bin'],
                    ['==', ['get', 'event_type'], eventType],
                ] as FilterSpecification,
                paint,
            },
            beforeId,
        );
    }

    for (const eventType of eventTypes) {
        const color = clusterColor(eventType, config);
        const visible = visibility[eventType] !== false ? 'visible' : 'none';
        const areaFilter: FilterSpecification = [
            'all',
            ['==', ['get', 'kind'], 'cluster-area'],
            ['==', ['get', 'event_type'], eventType],
        ];
        const markerFilter: FilterSpecification = [
            'all',
            ['==', ['get', 'kind'], 'cluster'],
            ['==', ['get', 'event_type'], eventType],
        ];

        const areaId = areaLayerId(eventType);
        if (!map.getLayer(areaId)) {
            map.addLayer({
                id: areaId,
                type: 'fill',
                source: SOURCE_ID,
                layout: { visibility: visible },
                filter: areaFilter,
                paint: {
                    'fill-color': color,
                    'fill-opacity': CLUSTER_FILL_OPACITY,
                },
            });
        }

        const outlineId = outlineLayerId(eventType);
        if (!map.getLayer(outlineId)) {
            map.addLayer({
                id: outlineId,
                type: 'line',
                source: SOURCE_ID,
                layout: { visibility: visible },
                filter: areaFilter,
                paint: {
                    'line-color': color,
                    'line-opacity': CLUSTER_OUTLINE_OPACITY,
                    'line-width': CLUSTER_OUTLINE_WIDTH,
                },
            });
        }

        const markerId = markerLayerId(eventType);
        if (!map.getLayer(markerId)) {
            // Append on top; raiseHeatmapClusterOverlayLayers reorders fill→outline→markers→hover.
            map.addLayer({
                id: markerId,
                type: 'symbol',
                source: SOURCE_ID,
                layout: {
                    visibility: visible,
                    'icon-image': markerImageId(eventType),
                    'icon-size': ICON_SCALE,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                    'symbol-sort-key': DEFAULT_MARKER_SORT_KEY,
                },
                filter: markerFilter,
            });
        }
    }

    if (!map.getLayer(MARKER_HOVER_LAYER_ID)) {
        map.addLayer({
            id: MARKER_HOVER_LAYER_ID,
            type: 'symbol',
            source: SOURCE_ID,
            layout: {
                visibility: 'visible',
                'icon-image': buildHoverIconImageExpression(eventTypes),
                'icon-size': ICON_SCALE_HOVERED,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'symbol-sort-key': HOVERED_MARKER_SORT_KEY,
            },
            filter: HOVER_FILTER_NONE,
        });
    }

    raiseHeatmapClusterOverlayLayers(map, eventTypes);
}
